import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BackButton } from "@/components/BackButton";
import { api, errMsg, today } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Field, Select } from "@/components/common";

const SRC = ["Pharmacy call", "Pharmacy portal", "Pharmacy fax", "Patient report", "Legacy import", "Imported CSV Refill History", "Other"];

export function schemaFor(kind, meta, rxOpts = []) {
  const meds = (meta?.medications || []).filter((m) => m.active !== false).map((m) => m.name);
  const inj = (meta?.medications || []).filter((m) => m.type === "injectable").map((m) => m.name);
  return {
    prescriptions: [["rx_date", "Prescription Date", "date"], ["medication", "Medication", meds, true], ["sent_date", "Prescription Sent Date", "date", true],
      ["provider", "Prescribing Provider"], ["pharmacy", "Pharmacy"], ["status", "Prescription Status", meta?.rx_statuses || []],
      ["supply_days", "Supply Duration (days)", "number"], ["refill_number", "Refill Number", "number"], ["confirmation_source", "Confirmation Source", SRC], ["notes", "Notes", "textarea"]],
    dispenses: [["medication", "Medication", meds, true], ["fill_date", "Fill Date", "date", true], ["prescription_id", "Associated Prescription", rxOpts], ["confirmed", "Pharmacy Confirmed", "checkbox"], ["pharmacy", "Pharmacy"],
      ["confirmation_source", "Confirmation Source", SRC], ["notes", "Notes", "textarea"]],
    injections: [["medication", "Medication", inj, true], ["scheduled_date", "Scheduled Administration Date", "date"],
      ["status", "Status", ["Scheduled", "Administered", "Missed", "Cancelled"]], ["administered_date", "Administered Date (required to confirm)", "date"],
      ["lot", "Lot Number"], ["notes", "Notes", "textarea"]],
    labs: [["lab_date", "Laboratory Date", "date", true], ["next_due", "Next Laboratory Due Date", "date"],
      ["status", "Completion Status", ["Completed", "Scheduled", "Pending results", "Cancelled"]], ["external", "External Laboratory", "checkbox"], ["notes", "Laboratory Notes", "textarea"]],
    appointments: [["type", "Appointment Type", meta?.appointment_types || [], true], ["scheduled_at", "Date & Time", "datetime-local", true],
      ["status", "Status", meta?.appointment_statuses || []], ["notes", "Notes", "textarea"]],
    tasks: [["type", "Task Type", meta?.task_types || [], true], ["due_date", "Due Date", "date", true], ["priority", "Priority", ["Low", "Medium", "High"]],
      ["status", "Status", ["Pending", "In Progress", "Completed", "Overdue"]], ["employee_id", "Assigned Employee", (meta?.staff || []).map((s) => ({ value: s.id, label: s.name }))], ["notes", "Notes", "textarea"]],
    notes: [["text", "Note", "textarea", true]],
  }[kind];
}

export const KIND_TITLES = { prescriptions: "Prescription", dispenses: "Pharmacy-Confirmed Fill", injections: "Injection", labs: "Laboratory Record",
  appointments: "Appointment", tasks: "Follow-up Task", notes: "Note" };

const defaults = (kind) => ({
  prescriptions: { sent_date: today(), status: "Sent", refill_number: 0 }, dispenses: { fill_date: today(), refill_number: 0, fill_count: 1, confirmed: true, confirmation_source: "Pharmacy call" },
  injections: { status: "Scheduled" }, labs: { lab_date: today(), status: "Completed" }, appointments: { status: "Scheduled" },
  tasks: { status: "Pending", priority: "Medium", due_date: today() }, notes: {},
}[kind]);

export function RecordDialog({ kind, patientId, record, open, onClose, onSaved, presetMedication, backLabel, prescriptions = [] }) {
  const { meta } = useApp();
  const [f, setF] = useState({});
  const [pid, setPid] = useState(patientId || "");
  const [patients, setPatients] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(record ? { ...record, scheduled_at: record.scheduled_at?.slice(0, 16), ...(kind === "dispenses" ? { confirmed: record.confirmed !== false, fill_count: record.fill_count || 1 } : {}) } : { ...defaults(kind), ...(presetMedication ? { medication: presetMedication } : {}) });
    setPid(patientId || record?.patient_id || "");
    if (!patientId && !record) api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
  }, [open, record, kind, patientId, presetMedication]);
  if (!kind) return null;
  const schema = schemaFor(kind, meta, prescriptions.filter((r) => r.status !== "Cancelled").map((r) => ({ value: r.id, label: `${r.medication} · Rx ${r.sent_date ? `${r.sent_date.slice(5, 7)}/${r.sent_date.slice(8, 10)}/${r.sent_date.slice(0, 4)}` : ""}` }))) || [];
  const save = async () => {
    if (kind === "dispenses" && f.confirmed && !f.confirmation_source) return toast.error("Select a Confirmation Source for a pharmacy-confirmed fill");
    setBusy(true);
    try {
      const { data } = record ? await api.put(`/records/${kind}/${record.id}`, f) : await api.post(`/patients/${pid}/records/${kind}`, f);
      toast.success(`${KIND_TITLES[kind]} saved`);
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid={`record-dialog-${kind}`}>
        {backLabel && <BackButton label={backLabel} onClick={onClose} testid="record-dialog-back-btn" className="-ml-1.5 self-start" />}
        <DialogHeader><DialogTitle>{record ? "Edit" : "Add"} {KIND_TITLES[kind]}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {!patientId && !record && (
            <Field label="Patient" required className="sm:col-span-2">
              <Select value={pid} onChange={setPid} placeholder="Select patient" testid="record-patient-select"
                options={patients.map((p) => ({ value: p.id, label: `${p.patient_id} · ${p.medication || ""}` }))} />
            </Field>
          )}
          {schema.map(([key, label, type, req]) => (
            <Field key={key} label={label} required={req} className={type === "textarea" ? "sm:col-span-2" : ""}>
              {Array.isArray(type) ? (
                <Select value={f[key]} onChange={(v) => setF({ ...f, [key]: v })} options={type} placeholder="—" testid={`record-field-${key}`} />
              ) : type === "textarea" ? (
                <textarea className="inp" rows={3} value={f[key] || ""} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`record-field-${key}`} />
              ) : type === "checkbox" ? (
                <input type="checkbox" className="h-4 w-4" checked={!!f[key]} onChange={(e) => setF({ ...f, [key]: e.target.checked })} data-testid={`record-field-${key}`} />
              ) : (
                <input className="inp" type={type || "text"} value={f[key] ?? ""} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`record-field-${key}`} />
              )}
            </Field>
          ))}
          {kind === "dispenses" && record && (
            <Field label="Reason for change" className="sm:col-span-2"><input className="inp" value={f.correction_reason || ""} onChange={(e) => setF({ ...f, correction_reason: e.target.value })} placeholder="Optional — saved in the audit log" data-testid="record-field-correction_reason" /></Field>
          )}
        </div>
        {kind === "dispenses" && f.fill_date > today() && <p className="text-xs text-amber-700" data-testid="record-scheduled-note">Future date: saved as a Scheduled fill and not counted until it is dispensed and confirmed.</p>}
        {kind === "injections" && <p className="text-xs text-[var(--muted-ink)]">An injection administration is kept as a clinical record; fill totals count recorded fill dates (1 per date).</p>}
        <div className="mt-2 flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose} data-testid="record-cancel-btn">Cancel</button>
          <button className="btn btn-primary" disabled={busy || (!pid && !record)} onClick={save} data-testid="record-save-btn">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
